import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, TextField
} from '@mui/material';
import { Close, CameraAlt, QrCodeScanner, Star } from '@mui/icons-material';
import { getProductByBarcode } from '../utils/productAPI';
import { isSubscriptionActive } from '../utils/stripeHelper';

function BarcodeScanner({ open, onClose, onScanSuccess, familyData }) {
  const isPremium = isSubscriptionActive(familyData);
  const [status, setStatus] = useState('idle'); // idle | scanning | looking_up | error | success
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const checkPremiumAccess = () => {
    if (!isPremium) {
      setShowUpgradeDialog(true);
      return false;
    }
    return true;
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const handleClose = () => {
    stopCamera();
    setStatus('idle');
    setError('');
    setManualCode('');
    setScannedCode('');
    onClose();
  };

  // --- Native Barcode Detection API (Chrome Android) ---
  const startCamera = async () => {
    if (!checkPremiumAccess()) return;
    
    setError('');
    setStatus('scanning');

    try {
      // Check native Barcode Detection API support
      if (!('BarcodeDetector' in window)) {
        setError('Camera scanning not supported in this browser. Use "Upload Photo" or enter barcode manually below.');
        setStatus('error');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const detector = new window.BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code']
      });

      const scan = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const code = barcodes[0].rawValue;
            stopCamera();
            await lookupBarcode(code);
            return;
          }
        } catch {}
        if (streamRef.current) {
          requestAnimationFrame(scan);
        }
      };
      requestAnimationFrame(scan);

    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access and try again, or use "Upload Photo" below.');
      } else {
        setError(`Camera failed: ${err.message}. Try "Upload Photo" or enter barcode manually.`);
      }
      setStatus('error');
    }
  };

  // --- File/Photo Upload fallback ---
  const handleFileUpload = async (e) => {
    if (!checkPremiumAccess()) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setStatus('scanning');

    try {
      if (!('BarcodeDetector' in window)) {
        setError('Barcode detection not supported. Please enter the barcode number manually.');
        setStatus('error');
        return;
      }

      const detector = new window.BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code']
      });

      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise(resolve => { img.onload = resolve; });

      const barcodes = await detector.detect(img);
      URL.revokeObjectURL(img.src);

      if (barcodes.length > 0) {
        await lookupBarcode(barcodes[0].rawValue);
      } else {
        setError('No barcode found in image. Try a clearer photo or enter the number manually.');
        setStatus('error');
      }
    } catch (err) {
      console.error('File scan error:', err);
      setError('Could not scan image. Please enter the barcode number manually.');
      setStatus('error');
    }
  };

  const lookupBarcode = async (code) => {
    setScannedCode(code);
    setStatus('looking_up');
    stopCamera();

    try {
      const product = await getProductByBarcode(code);
      if (product) {
        onScanSuccess({
          barcode: product.barcode,
          name: product.name,
          brand: product.brand,
          image: product.image,
          price: product.estimatedPrice,
          found: true
        });
        handleClose();
      } else {
        onScanSuccess({ barcode: code, name: `Product ${code}`, found: false });
        setError(`Scanned: ${code} — not in database. Added as unknown product.`);
        setStatus('error');
      }
    } catch (err) {
      onScanSuccess({ barcode: code, name: `Product ${code}`, found: false });
      handleClose();
    }
  };

  const handleManualSubmit = () => {
    if (manualCode.trim()) lookupBarcode(manualCode.trim());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={window.innerWidth < 600}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCodeScanner /> Scan Barcode
          </Box>
          <Button onClick={handleClose} sx={{ minWidth: 'auto', p: 1 }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2, textAlign: 'center' }}>

          {/* Video feed when scanning */}
          {status === 'scanning' && (
            <Box sx={{ mb: 2 }}>
              <video
                ref={videoRef}
                muted
                playsInline
                style={{ width: '100%', maxWidth: 400, borderRadius: 8, background: '#000' }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Point camera at barcode
              </Typography>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => { stopCamera(); setStatus('idle'); }}>
                Cancel Camera
              </Button>
            </Box>
          )}

          {/* Looking up */}
          {status === 'looking_up' && (
            <Box sx={{ py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Looking up barcode {scannedCode}...</Typography>
            </Box>
          )}

          {/* Idle / Error state */}
          {(status === 'idle' || status === 'error') && (
            <Box>
              {error && (
                <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>
              )}

              {/* Option 1: Live Camera */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<CameraAlt />}
                onClick={startCamera}
                sx={{ mb: 2 }}
              >
                Scan with Camera
              </Button>

              {/* Option 2: Upload photo of barcode */}
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<CameraAlt />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 3 }}
              >
                Upload Photo of Barcode
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />

              {/* Option 3: Manual entry */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Or type the barcode number (under the stripes):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  size="small"
                  placeholder="e.g. 012345678901"
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleManualSubmit()}
                  fullWidth
                  inputProps={{ inputMode: 'numeric' }}
                />
                <Button
                  variant="contained"
                  onClick={handleManualSubmit}
                  disabled={!manualCode.trim()}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>Cancel</Button>
      </DialogActions>

      {/* Upgrade Dialog for Non-Premium Users */}
      <Dialog open={showUpgradeDialog} onClose={() => setShowUpgradeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: 'warning.main' }} />
            Unlock Barcode Scanner
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Barcode scanning is a <strong>Premium feature</strong>! Upgrade to Earn-E Premium to:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">📱 Scan any product barcode instantly</Typography>
            <Typography variant="body2">🤖 Get AI-powered product details</Typography>
            <Typography variant="body2">💰 Auto-add estimated prices</Typography>
            <Typography variant="body2">⚡ Never type barcodes manually again</Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🎉 Start your <strong>14-day free trial</strong> - cancel anytime!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowUpgradeDialog(false)} variant="outlined">
            Maybe Later
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowUpgradeDialog(false);
              window.location.href = '/subscription';
            }}
            startIcon={<Star />}
          >
            Start Free Trial
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default BarcodeScanner;
